import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetAirports from "../../hooks/airports/useGetAirports";
import useDeleteAirport from "../../hooks/airports/useDeleteAirport";
import useDeleteAllAirports from "../../hooks/airports/useDeleteAllAirports";
import useRestoreAirport from "../../hooks/airports/useRestoreAirport";
import useRestoreAllAirports from "../../hooks/airports/useRestoreAllAirports";
import useForceDeleteAirport from "../../hooks/airports/useForceDeleteAirport";
import useForceDeleteAllAirports from "../../hooks/airports/useForceDeleteAllAirports";

import useGetCountriesForSelect from "../../hooks/countries/useGetCountriesForSelect";
import useGetCitiesForSelect from "../../hooks/cities/useGetCitiesForSelect";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import AirportsTableRow from "./Table/AirportsTableRow";
import AirportsTableColumns from "./Table/AirportsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const AirportsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("airports_list"),
            create: has("airports_create"),
            edit: has("airports_edit"),
            delete: has("airports_delete"),
            forceDelete: has("airports_force_delete"),
            archiveList: has("airports_archive_list"),
            restore: has("airports_restore"),
            restoreAll: has("airports_restore-all-table"),
            deleteAll: has("airports_delete-all-table"),
            massForceDeleteAll: has("airports_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/airports/archive");

    // CRUD Hooks
    const { deleteAirport, isDelete } = useDeleteAirport();
    const { deleteAllAirports, isDeleteAll } = useDeleteAllAirports();
    const { restoreAirport, isRestore } = useRestoreAirport();
    const { restoreAllAirports, isRestoreAll } = useRestoreAllAirports();
    const { forceDeleteAirport, isForceDelete } = useForceDeleteAirport();
    const { forceDeleteAllAirports, isForceDeleteAll } = useForceDeleteAllAirports();

    // Table Actions Hook
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/airports",
        deleteSingle: deleteAirport,
        deleteAll: deleteAllAirports,
        restoreSingle: restoreAirport,
        restoreAll: restoreAllAirports,
        forceDeleteSingle: forceDeleteAirport,
        forceDeleteAll: forceDeleteAllAirports,
        isArchive,
    });

    // Local Filters
    const [statusFilter, setStatusFilter] = useState("");
    const [countryFilter, setCountryFilter] = useState("");
    const [cityFilter, setCityFilter] = useState("");

    const { options: countriesOptions } = useGetCountriesForSelect();
    const { options: citiesOptions } = useGetCitiesForSelect(countryFilter);

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            country_id: countryFilter || undefined,
            city_id: cityFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, countryFilter, cityFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
            {
                type: "select",
                name: "country_id",
                label: { ar: "الدولة", en: "Country" },
                options: [
                    { value: "", label: { ar: "كل الدول", en: "All Countries" } },
                    ...(countriesOptions || [])
                ],
                value: countryFilter,
                onChange: (val) => {
                    setCountryFilter(val);
                    setCityFilter(""); // Reset city when country changes
                },
            },
            {
                type: "select",
                name: "city_id",
                label: { ar: "المدينة", en: "City" },
                options: [
                    { value: "", label: { ar: "كل المدن", en: "All Cities" } },
                    ...(citiesOptions || [])
                ],
                value: cityFilter,
                onChange: setCityFilter,
                disabled: !countryFilter
            },
        ],
        [statusFilter, countryFilter, cityFilter, countriesOptions, citiesOptions]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
        setCountryFilter("");
        setCityFilter("");
    };

    const { isLoading, error, airports, meta } = useGetAirports(page, perPage, filters);
    const items = useMemo(() => airports || [], [airports]);

    const rowMenuDisabled = isArchive ? (!perms.restore && !perms.forceDelete) : (!perms.edit && !perms.delete);

    const renderRow = (params) => (
        <AirportsTableRow
            key={params.item.id}
            airport={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "المطارات" : "Airports";
    const subtitle = isArchive
        ? isArabic
            ? "عرض المطارات المحذوفة (الأرشيف)"
            : "Archived airports (soft deleted)"
        : isArabic
            ? "إدارة المطارات المتاحة في النظام"
            : "Manage available airports in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة مطار"
            : "Create new airport";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission");
                return;
            }
            if (isRestoreAll) return;
            if (!selectedIds.length) {
                toast.error(isArabic ? "الرجاء تحديد العناصر المراد استرجاعها أولاً" : "Please select items to restore first");
                return;
            }
            restoreAllAirports(selectedIds);
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission");
                return;
            }
            navigate("/airports/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack direction="row" justifyContent={isArabic ? "flex-start" : "flex-end"} sx={{ mb: 1 }}>
                    <ArchiveButton
                        to="/airports/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack direction="row" justifyContent={isArabic ? "flex-start" : "flex-end"} sx={{ mb: 1 }}>
                    <ArchiveButton
                        to="/airports"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={AirportsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية لتنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا يوجد مطارات في الأرشيف" : "لا يوجد مطارات"}
                emptyLabelEn={isArchive ? "No archived airports" : "No airports"}
                loadErrorAr="حدث خطاء اثناء تحميل المطارات"
                loadErrorEn="Failed to load airports"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default AirportsTable;
